/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Central HTTP response cache with in-flight request deduplication.
 *
 * KEY FEATURES:
 * - Deduplication: concurrent requests to the same URL share one HTTP call.
 * - TTL-based expiry: cached entries are automatically invalidated after a
 *   configurable time-to-live (default: 5 minutes).
 * - Manual invalidation: single entry, prefix-based, or full cache clear.
 * - Transparent integration: used internally by ApiClient.getShared(); feature
 *   services never interact with this service directly.
 *
 * DESIGN DECISIONS:
 * - `shareReplay(1)` with `refCount: false` keeps the source Observable alive
 *   until it completes naturally, so late subscribers always receive the cached
 *   value synchronously even after the HTTP call has finished.
 * - The cache stores the Observable itself (not the resolved value), so RxJS
 *   manages multicasting and backpressure.
 * - TTL is checked lazily on access, not via a background timer, to avoid
 *   memory leaks and unnecessary work in inactive tabs.
 */

import {Injectable} from '@angular/core';
import {Observable, shareReplay} from 'rxjs';
import {tap} from 'rxjs/operators';

/** Default cache entry lifetime in milliseconds (5 minutes). */
const DEFAULT_TTL_MS = 5 * 60 * 1_000;

/** Internal metadata stored alongside each cached Observable. */
interface CacheEntry<T> {
  /** The multicasted, replaying Observable returned to subscribers. */
  readonly response$: Observable<T>;
  /** Unix timestamp (ms) after which this entry is considered stale. */
  readonly expiresAt: number;
  /** Whether the underlying HTTP call has completed (success or error). */
  settled: boolean;
}

/** Options accepted by `RequestCacheService.get()`. */
export interface CacheGetOptions {
  /**
   * Time-to-live in milliseconds.
   * Overrides the service-level default for this specific entry.
   * Pass `Infinity` to cache permanently for the application lifetime.
   */
  ttlMs?: number;
}

@Injectable({providedIn: 'root'})
export class RequestCacheService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly cache = new Map<string, CacheEntry<any>>();

  /**
   * Returns a cached, deduplicated Observable for `key`.
   *
   * - If a fresh, non-expired entry exists → returns it immediately (0 HTTP calls).
   * - If an in-flight entry exists (not yet settled) → joins the existing call.
   * - Otherwise → calls `factory()` to create the source Observable, wraps it
   *   with `shareReplay(1)`, and stores it in the cache.
   *
   * @param key     - The deduplication and cache key (typically the full URL).
   * @param factory - Produces the cold source Observable when no entry exists.
   * @param options - Per-entry cache options (e.g. custom TTL).
   * @returns A hot, multicasted Observable that replays the last emitted value.
   */
  get<T>(
    key: string,
    factory: () => Observable<T>,
    options?: CacheGetOptions,
  ): Observable<T> {
    const existing = this.cache.get(key);

    // Reuse if: not yet expired OR still in-flight (not settled yet).
    if (existing && (!existing.settled || Date.now() < existing.expiresAt)) {
      return existing.response$ as Observable<T>;
    }

    const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
    const entry: CacheEntry<T> = {
      response$: factory().pipe(
        shareReplay({bufferSize: 1, refCount: false}),
        tap({
          // Mark as settled so TTL expiry check takes effect after completion.
          complete: () => {
            entry.settled = true;
          },
          // On error: evict immediately so the next call retries the request.
          error: () => {
            this.cache.delete(key);
          },
        }),
      ),
      expiresAt: Date.now() + ttlMs,
      settled: false,
    };

    this.cache.set(key, entry);
    return entry.response$;
  }

  /**
   * Invalidates the cache entry for an exact URL key.
   * The next call to `get()` with this key will trigger a new HTTP request.
   *
   * @param key - The exact URL string used as cache key.
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidates all cache entries whose key starts with `urlPrefix`.
   * Useful for invalidating a whole resource family after a mutation.
   *
   * @example
   * // Invalidate all passport schema entries after updating a schema
   * this.requestCache.invalidateByPrefix('/passport-management-service/definitions/schema');
   *
   * @param urlPrefix - URL prefix to match against stored cache keys.
   */
  invalidateByPrefix(urlPrefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(urlPrefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Removes all entries from the cache.
   * Call this on logout or when the active organisation changes.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Returns the number of entries currently held in the cache.
   * Intended for use in unit tests and diagnostics only.
   */
  get size(): number {
    return this.cache.size;
  }
}
